import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import { fmtDate } from "../../../format";
import type { Charger, FirmwareArtifact, FirmwareUpstream } from "../../../types";
import type { FirmwareDeployResponse } from "../types";

interface FirmwareManagementCardProps {
  firmwares: FirmwareArtifact[];
  upstreams: FirmwareUpstream[];
  onlineChargers: Charger[];
  uploadUpstreamId: number | "";
  uploadVersion: string;
  uploadFile: File | null;
  selectedFirmwareId: number | "";
  selectedChargePoints: string[];
  deployRetries: number | "";
  deployRetryInterval: number | "";
  deployResult: FirmwareDeployResponse | null;
  setUploadUpstreamId: (value: number | "") => void;
  setUploadVersion: (value: string) => void;
  setUploadFile: (value: File | null) => void;
  setSelectedFirmwareId: (value: number | "") => void;
  setSelectedChargePoints: Dispatch<SetStateAction<string[]>>;
  setDeployRetries: (value: number | "") => void;
  setDeployRetryInterval: (value: number | "") => void;
  onUploadFirmware: () => void;
  onDeployFirmware: () => void;
}

export function FirmwareManagementCard({
  firmwares,
  upstreams,
  onlineChargers,
  uploadUpstreamId,
  uploadVersion,
  uploadFile,
  selectedFirmwareId,
  selectedChargePoints,
  deployRetries,
  deployRetryInterval,
  deployResult,
  setUploadUpstreamId,
  setUploadVersion,
  setUploadFile,
  setSelectedFirmwareId,
  setSelectedChargePoints,
  setDeployRetries,
  setDeployRetryInterval,
  onUploadFirmware,
  onDeployFirmware,
}: FirmwareManagementCardProps) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>Firmware Management</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
          <TextField
            label="Firmware Upstream"
            value={uploadUpstreamId}
            onChange={(e) => setUploadUpstreamId(Number(e.target.value) || "")}
            select
            SelectProps={{ native: true }}
            fullWidth
          >
            <option value="">Select upstream</option>
            {upstreams.map((upstream) => (
              <option key={upstream.id} value={upstream.id}>
                {upstream.vendor} / {upstream.name}
              </option>
            ))}
          </TextField>
          <TextField
            label="Firmware Version"
            value={uploadVersion}
            onChange={(e) => setUploadVersion(e.target.value)}
            fullWidth
          />
          <Button variant="outlined" component="label">
            Select Binary
            <input
              hidden
              type="file"
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          <Button
            variant="contained"
            onClick={onUploadFirmware}
            disabled={!uploadUpstreamId || !uploadVersion || !uploadFile}
          >
            Upload
          </Button>
        </Stack>
        {uploadFile && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selected file: {uploadFile.name}
          </Typography>
        )}

        {firmwares.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No firmware artifacts uploaded yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Deploy</TableCell>
                <TableCell>Upstream</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firmwares.map((fw) => (
                <TableRow key={fw.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedFirmwareId === fw.id}
                      onChange={() => setSelectedFirmwareId(selectedFirmwareId === fw.id ? "" : fw.id)}
                    />
                  </TableCell>
                  <TableCell>{fw.upstream_vendor} / {fw.upstream_name}</TableCell>
                  <TableCell>{fw.version}</TableCell>
                  <TableCell>{fw.file_name}</TableCell>
                  <TableCell>{fmtDate(fw.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Fleet Deployment Target</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            {onlineChargers.map((charger) => (
              <FormControlLabel
                key={charger.charge_point_id}
                control={
                  <Checkbox
                    checked={selectedChargePoints.includes(charger.charge_point_id)}
                    onChange={(e) => {
                      setSelectedChargePoints((prev) =>
                        e.target.checked
                          ? [...prev, charger.charge_point_id]
                          : prev.filter((item) => item !== charger.charge_point_id)
                      );
                    }}
                  />
                }
                label={charger.charge_point_id}
              />
            ))}
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
            <TextField
              label="Retries (optional)"
              type="number"
              value={deployRetries}
              onChange={(e) => setDeployRetries(e.target.value === "" ? "" : Number(e.target.value))}
              inputProps={{ min: 0, max: 10 }}
              fullWidth
            />
            <TextField
              label="Retry Interval Seconds (optional)"
              type="number"
              value={deployRetryInterval}
              onChange={(e) => setDeployRetryInterval(e.target.value === "" ? "" : Number(e.target.value))}
              inputProps={{ min: 0, max: 3600 }}
              fullWidth
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Leave charger selection empty to target all chargers whose model is mapped to the selected firmware upstream.
          </Typography>
          <Button variant="contained" onClick={onDeployFirmware} disabled={!selectedFirmwareId}>
            Deploy Firmware
          </Button>
        </Box>

        {deployResult && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Dispatched {deployResult.dispatched}/{deployResult.total_targets}
            {deployResult.offline.length > 0 ? ` • Offline: ${deployResult.offline.join(", ")}` : ""}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
